import { useDisclosure } from '@heroui/react';
import { toast } from 'react-toastify';
import { useState, useCallback } from 'react';
import { useAtom } from 'jotai';

import { assignLeadOwner, assignLeadToSelf, updateLeadStatus, leadDetailIdAtom } from '@/store';
import type { Lead, LeadStatus } from '@/types';

export function useLeadAssignOwner() {
  const [, assignOwner] = useAtom(assignLeadOwner);
  const { isOpen: isModalOpen, onOpen: onModalOpen, onOpenChange: onModalOpenChange } = useDisclosure();
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const openModal = useCallback(
    (lead: Lead) => {
      setSelectedLead(lead);
      onModalOpen();
    },
    [onModalOpen]
  );

  const handleAssign = useCallback(
    async (ownerId: number) => {
      if (!selectedLead) return;

      try {
        setIsSubmitting(true);
        await assignOwner({ leadId: selectedLead.id, ownerId });
        toast.success('Owner assigned successfully');
        setIsSubmitting(false);
        onModalOpenChange();
        setSelectedLead(null);
      } catch (error) {
        setIsSubmitting(false);
        // Error is handled by axios interceptor
      }
    },
    [selectedLead, onModalOpenChange, assignOwner]
  );

  return {
    isModalOpen,
    selectedLead,
    isSubmitting,
    openModal,
    handleAssign,
    onModalOpenChange
  };
}

export function useLeadAssignSelf() {
  const [, assignSelf] = useAtom(assignLeadToSelf);
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const openConfirm = useCallback(
    (lead: Lead) => {
      setSelectedLead(lead);
      onOpen();
    },
    [onOpen]
  );

  const handleAssignSelf = useCallback(async () => {
    if (!selectedLead) return;

    try {
      setIsSubmitting(true);
      await assignSelf({ leadId: selectedLead.id });
      toast.success('Lead assigned to you successfully');
      setIsSubmitting(false);
      onOpenChange();
      setSelectedLead(null);
    } catch (error) {
      setIsSubmitting(false);
    }
  }, [selectedLead, assignSelf, onOpenChange]);

  return {
    isConfirmOpen: isOpen,
    selectedLead,
    isSubmitting,
    openConfirm,
    onConfirm: handleAssignSelf,
    onOpenChange,
  };
}

export function useLeadStatusChange() {
  const [, updateStatus] = useAtom(updateLeadStatus);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleStatusChange = useCallback(
    async (leadId: number, status: LeadStatus) => {
      try {
        setIsUpdating(true);
        await updateStatus({ leadId, status });
        toast.success('Status updated successfully');
        setIsUpdating(false);
      } catch (error) {
        setIsUpdating(false);
        // Error is handled by axios interceptor
      }
    },
    [updateStatus]
  );

  return {
    isUpdating,
    handleStatusChange
  };
}

